import { api } from "@/services/api";
import { TagTypes } from "@/services/tagTypes";
import { APIS } from "@/app/app-config";
import { CreateYardJobRequest } from "../types/jobs";
import {
  ListSupportedYardIdentifiersRequest,
  ListSupportedYardIdentifiersResponse,
  GetYardMapRequest,
  GetYardMapResponse,
  YardMap,
  QueryYardGraphResponse,
  QueryYardGraphRequest,
  ListEquipmentOwnersResponse,
  UpdateEquipmentLocationRequest,
  UpdateEquipmentLocationResponse,
  ListEligibleLocationsForEquipmentRequest,
  ListEligibleLocationsForEquipmentResponse,
  AuditRemoveEquipmentFromYardRequest,
  AuditRemoveEquipmentFromYardResponse,
  CancelMoveRequest,
  CancelMoveResponse,
  CompleteMoveRequest,
  CompleteMoveResponse,
  ReorderMoveRequest,
  ReorderMoveResponse,
  ListPendingMovesRequest,
  ListPendingMovesResponse,
  AuditAddEquipmentToYardRequest,
  AuditAddEquipmentToYardResponse,
  ForceCheckOutRequest,
  ForceCheckOutResponse,
  EmergencyTrailerHandoverStatusUpdateRequest,
  EmergencyTrailerHandoverStatusUpdateResponse,
} from "@amzn/yt-yard-console-service-typescript-client/com/amazon/ytyardconsoleservice";

import type {
  GetYardVisitsInput,
  GetYardVisitsOutput,
  GetYardJobsInput,
  GetYardJobsOutput,
  CreateYardJobOutput,
  GetYardAlertsInput,
  GetYardAlertsOutput,
  CompleteYardAlertInput,
  CompleteYardAlertOutput,
  DismissYardAlertOutput,
  YardAlert,
  BookVRInput,
  BookVROutput,
} from "@amzn/yt-yard360-service-endpoint-ts-client";
import { ListSupportedEquipmentTypesResponse } from "@/features/relayYardControl/types/equipmentTypeMetadata";
import type {
  GetYardMovesInput,
  GetYardMovesOutput,
} from "@/features/relayYardControl/types/moves";
import type { ContentTag, YardVisit } from "@/features/relayYardControl/types/visits";
import type { YardStateResponse } from "@/features/relayYardControl/types/yardState";

// Re-export for convenience in consumers
export type { ListEligibleLocationsForEquipmentRequest, ListEligibleLocationsForEquipmentResponse };

export interface FleetEquipmentRecord {
  aapId: string;
  vehicleNumber: string;
}

/** Response from getEquipmentById — full equipment details from YER via Yard Console */
export interface FleetEquipmentDetails {
  equipmentId: string;
  equipmentVersion: string;
  ownerCode: string;
  equipmentType: string;
  countryCode?: string;
  subnationalEntity?: string;
  licensePlateNumber?: string;
  operationalInfo?: Array<{ operationalCode: string }>;
}

interface AssetUniqueIdentifiers {
  aapId?: string;
  type?: string;
  vehicleNumber?: string;
  owner?: unknown;
}

interface GetAllFleetEquipmentResponse {
  assetsUniqueIdentifiers?: AssetUniqueIdentifiers[];
  exclusiveStartKeyEquipmentId?: string;
  exclusiveStartKeyFleetNameAndId?: string;
}

/**
 * Fetches all fleet equipment records via paginated API calls.
 * Exported for testability — used by the getAllFleetEquipment RTK Query endpoint.
 */
export async function fetchAllFleetEquipment(
  fetchWithBQ: (arg: {
    apiName: string;
    url: string;
    method: string;
    body: Record<string, unknown>;
  }) => Promise<{ data?: unknown; error?: unknown }>
): Promise<{ data: FleetEquipmentRecord[] } | { error: unknown }> {
  const allRecords: FleetEquipmentRecord[] = [];
  let startKeyEquipmentId: string | undefined;
  let startKeyFleetNameAndId: string | undefined;

  do {
    const result = await fetchWithBQ({
      apiName: APIS.GetAllFleetEquipment.name,
      url: APIS.GetAllFleetEquipment.resource,
      method: "POST",
      body: {
        exclusiveStartKeyEquipmentId: startKeyEquipmentId,
        exclusiveStartKeyFleetNameAndId: startKeyFleetNameAndId,
      },
    });
    if (result.error) return { error: result.error };
    const data = result.data as GetAllFleetEquipmentResponse;
    if (data.assetsUniqueIdentifiers) {
      allRecords.push(
        ...data.assetsUniqueIdentifiers
          .filter((a) => a.vehicleNumber)
          .map((a) => ({ aapId: a.aapId ?? "", vehicleNumber: a.vehicleNumber! }))
      );
    }
    startKeyEquipmentId = data.exclusiveStartKeyEquipmentId ?? undefined;
    startKeyFleetNameAndId = data.exclusiveStartKeyFleetNameAndId ?? undefined;
  } while (startKeyEquipmentId && startKeyFleetNameAndId);

  return { data: allRecords };
}

export const yardApis = api.injectEndpoints({
  endpoints: (builder) => ({
    getListSupportedYardIdentifiers: builder.query<
      ListSupportedYardIdentifiersResponse,
      ListSupportedYardIdentifiersRequest
    >({
      query: (searchQuery: ListSupportedYardIdentifiersRequest = {}) => ({
        apiName: APIS.ListSupportedYardIdentifiers.name,
        url: APIS.ListSupportedYardIdentifiers.resource,
        method: "POST",
        body: searchQuery,
      }),
      providesTags: [TagTypes.YardConfig],
    }),
    getListEquipmentOwners: builder.query<ListEquipmentOwnersResponse, { buildingCode?: string }>({
      query: ({ buildingCode }) => ({
        apiName: APIS.ListEquipmentOwners.name,
        url: APIS.ListEquipmentOwners.resource,
        method: "POST",
        body: {
          requester: { system: "RYC" },
          buildingCode,
        },
      }),
      providesTags: [TagTypes.YardConfig],
    }),
    getQueryYardGraph: builder.query<QueryYardGraphResponse, QueryYardGraphRequest>({
      query: (searchQuery) => ({
        apiName: APIS.QueryYardGraph.name,
        url: APIS.QueryYardGraph.resource,
        method: "POST",
        body: searchQuery,
      }),
      providesTags: [TagTypes.YardConfig, TagTypes.YardGraph],
    }),
    getYardMap: builder.query<YardMap, GetYardMapRequest>({
      query: (yardId: GetYardMapRequest) => ({
        apiName: APIS.GetYardMap.name,
        url: APIS.GetYardMap.resource,
        method: "POST",
        body: yardId,
      }),
      providesTags: [TagTypes.YardMap],
      transformResponse: (response: GetYardMapResponse): YardMap => {
        return response.yardMap;
      },
    }),
    getYardVisits: builder.query<YardVisit[], GetYardVisitsInput>({
      query: (searchQuery) => ({
        apiName: APIS.GetYardVisits.name,
        url: APIS.GetYardVisits.resource,
        method: "POST",
        body: searchQuery,
      }),
      providesTags: [TagTypes.YardVisits],
      // Cast at the API boundary — Per-feature shape requirements live in selectors
      transformResponse: (response: GetYardVisitsOutput): YardVisit[] => {
        return (response.visits ?? []) as YardVisit[];
      },
    }),
    createYardJob: builder.mutation<CreateYardJobOutput, CreateYardJobRequest>({
      query: (searchQuery) => ({
        apiName: APIS.CreateYardJob.name,
        url: APIS.CreateYardJob.resource,
        method: "POST",
        body: searchQuery,
      }),
      invalidatesTags: [TagTypes.YardJobs, TagTypes.YardMoves],
    }),
    getYardAlerts: builder.query<YardAlert[], GetYardAlertsInput>({
      query: (searchQuery) => ({
        apiName: APIS.GetYardAlerts.name,
        url: APIS.GetYardAlerts.resource,
        method: "POST",
        body: searchQuery,
      }),
      transformResponse: (response: GetYardAlertsOutput): YardAlert[] => {
        return response.alerts || [];
      },
      providesTags: [TagTypes.YardAlerts],
    }),
    getYardJobs: builder.query<GetYardJobsOutput["jobs"], GetYardJobsInput>({
      query: (searchQuery) => ({
        apiName: APIS.GetYardJobs.name,
        url: APIS.GetYardJobs.resource,
        method: "POST",
        body: searchQuery,
      }),
      transformResponse: (response: GetYardJobsOutput): GetYardJobsOutput["jobs"] => {
        return response.jobs;
      },
      providesTags: [TagTypes.YardJobs],
    }),
    getYardMetrics: builder.query<any, any>({
      query: (searchQuery) => ({
        apiName: APIS.GetYardMetrics.name,
        url: APIS.GetYardMetrics.resource,
        method: "POST",
        body: searchQuery,
      }),
      providesTags: [TagTypes.YardMetrics],
    }),
    getYardMoves: builder.query<GetYardMovesOutput, GetYardMovesInput>({
      query: ({ yardId }) => ({
        apiName: APIS.GetYardMoves.name,
        url: APIS.GetYardMoves.resource,
        method: "POST",
        body: { yardId },
      }),
      providesTags: [TagTypes.YardMoves],
    }),
    completeYardAlert: builder.mutation<CompleteYardAlertOutput, CompleteYardAlertInput>({
      query: (searchQuery) => ({
        apiName: APIS.CompleteYardAlert.name,
        url: APIS.CompleteYardAlert.resource,
        method: "POST",
        body: searchQuery,
      }),
    }),
    dismissYardAlert: builder.mutation<DismissYardAlertOutput, any>({
      query: (searchQuery) => ({
        apiName: APIS.DismissYardAlert.name,
        url: APIS.DismissYardAlert.resource,
        method: "POST",
        body: searchQuery,
      }),
    }),
    BookVR: builder.mutation<BookVROutput, BookVRInput>({
      query: (searchQuery) => ({
        apiName: APIS.BookVR.name,
        url: APIS.BookVR.resource,
        method: "POST",
        body: searchQuery,
      }),
    }),
    updateEquipmentLocation: builder.mutation<
      UpdateEquipmentLocationResponse,
      UpdateEquipmentLocationRequest
    >({
      query: (request) => ({
        apiName: APIS.UpdateEquipmentLocation.name,
        url: APIS.UpdateEquipmentLocation.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      invalidatesTags: [TagTypes.YardMap, TagTypes.YardMoves, TagTypes.YardAlerts],
    }),
    updateEquipmentCoreInfo: builder.mutation<
      unknown,
      {
        yardId: { buildingCode: string };
        visitId: string;
        equipmentCoreInfo: {
          equipmentId: string;
          equipmentVersion: string;
          equipmentType?: string;
          ownerCode?: string;
          equipmentNumber?: string;
          countryCode?: string;
          subnationalEntity?: string;
          licensePlateNumber?: string;
          fleetSystemName?: string;
        };
        requester: { system: string };
      }
    >({
      query: (request) => ({
        apiName: APIS.UpdateEquipmentCoreInfo.name,
        url: APIS.UpdateEquipmentCoreInfo.resource,
        method: "POST",
        body: request,
        requester: { system: "RYC" },
      }),
    }),
    updateEquipmentOtherInfo: builder.mutation<
      unknown,
      {
        yardId: { buildingCode: string };
        visitId: string;
        equipmentOtherInfo: {
          operator?: string;
          activeVisitReasons?: string;
          equipmentSeals?: string[];
          equipmentEmptyStatus?: string;
          notes?: string;
          haulActionIdentifiers?: unknown;
          rawVRIdOrISA?: unknown;
          contentTags?: ContentTag[];
        };
      }
    >({
      query: (request) => ({
        apiName: APIS.UpdateEquipmentOtherInfo.name,
        url: APIS.UpdateEquipmentOtherInfo.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
    }),
    listEligibleLocationsForEquipment: builder.query<
      ListEligibleLocationsForEquipmentResponse,
      ListEligibleLocationsForEquipmentRequest
    >({
      query: (request) => ({
        apiName: APIS.ListEligibleLocationsForEquipment.name,
        url: APIS.ListEligibleLocationsForEquipment.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
    }),
    addOperationalCodes: builder.mutation<
      unknown,
      {
        yardId: { buildingCode: string };
        equipmentIdentifier: { equipmentId: string; equipmentVersion: string };
        visitId: string;
        operationalCodes: string[];
      }
    >({
      query: (request) => ({
        apiName: APIS.AddOperationalCodes.name,
        url: APIS.AddOperationalCodes.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
    }),
    removeOperationalCodes: builder.mutation<
      unknown,
      {
        yardId: { buildingCode: string };
        equipmentIdentifier: { equipmentId: string; equipmentVersion: string };
        visitId: string;
        operationalCodes: string[];
      }
    >({
      query: (request) => ({
        apiName: APIS.RemoveOperationalCodes.name,
        url: APIS.RemoveOperationalCodes.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
    }),
    listSupportedEquipmentTypes: builder.query<ListSupportedEquipmentTypesResponse, void>({
      query: () => ({
        apiName: APIS.ListSupportedEquipmentTypes.name,
        url: APIS.ListSupportedEquipmentTypes.resource,
        method: "POST",
        body: {},
      }),
      providesTags: [TagTypes.YardConfig],
      keepUnusedDataFor: 10800, // 3 hours — equipment types rarely change
    }),
    getAllFleetEquipment: builder.query<FleetEquipmentRecord[], void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const result = await fetchAllFleetEquipment(
          fetchWithBQ as Parameters<typeof fetchAllFleetEquipment>[0]
        );
        if ("error" in result) {
          return {
            error: result.error as ReturnType<typeof fetchWithBQ> extends Promise<infer R>
              ? R extends { error: infer E }
                ? E
                : never
              : never,
          };
        }
        return result;
      },
      providesTags: [TagTypes.YardConfig],
      keepUnusedDataFor: 10800, // Cache fleet equipment for 3 hours
    }),
    getEquipmentByFleetId: builder.query<FleetEquipmentDetails, { fleetId: string }>({
      query: ({ fleetId }) => ({
        apiName: APIS.GetEquipmentById.name,
        url: APIS.GetEquipmentById.resource,
        method: "POST",
        body: { fleetId },
      }),
      transformResponse: (response: { equipment: FleetEquipmentDetails }): FleetEquipmentDetails =>
        response.equipment,
    }),
    auditRemoveEquipmentFromYard: builder.mutation<
      AuditRemoveEquipmentFromYardResponse,
      AuditRemoveEquipmentFromYardRequest
    >({
      query: (request) => ({
        apiName: APIS.AuditRemoveEquipmentFromYard.name,
        url: APIS.AuditRemoveEquipmentFromYard.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      // Removing a vehicle from the yard can update the metrics, so we invalidate
      // the tag. We don't need to invalidate yard visits, since we can optimistically
      // remove the visit from the store upon successful API response.
      invalidatesTags: [TagTypes.YardMetrics],
    }),
    cancelMove: builder.mutation<CancelMoveResponse, CancelMoveRequest>({
      query: (request) => ({
        apiName: APIS.CancelMove.name,
        url: APIS.CancelMove.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      invalidatesTags: [TagTypes.YardMoves],
    }),
    completeMove: builder.mutation<CompleteMoveResponse, CompleteMoveRequest>({
      query: (request) => ({
        apiName: APIS.CompleteMove.name,
        url: APIS.CompleteMove.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      invalidatesTags: [TagTypes.YardMoves],
    }),
    reorderMove: builder.mutation<ReorderMoveResponse, ReorderMoveRequest>({
      query: (request) => ({
        apiName: APIS.ReorderMove.name,
        url: APIS.ReorderMove.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      // invalidatesTags: [TagTypes.YardMoves],
    }),
    listPendingMoves: builder.query<ListPendingMovesResponse, ListPendingMovesRequest>({
      query: (request) => ({
        apiName: APIS.ListPendingMoves.name,
        url: APIS.ListPendingMoves.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
    }),
    getYardStateWithPendingMoves: builder.query<YardStateResponse, { buildingCode: string }>({
      query: ({ buildingCode }) => ({
        apiName: APIS.GetYardStateWithPendingMoves.name,
        url: APIS.GetYardStateWithPendingMoves.resource,
        method: "POST",
        body: {
          yardId: { buildingCode },
          requester: { system: "RYC" },
        },
      }),
    }),
    auditAddEquipmentToYard: builder.mutation<
      AuditAddEquipmentToYardResponse,
      AuditAddEquipmentToYardRequest
    >({
      query: (request) => ({
        apiName: APIS.AuditAddEquipmentToYard.name,
        url: APIS.AuditAddEquipmentToYard.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      // Adding a vehicle can invalidate the yard metrics, so we invalidate the
      // current metrics. We cannot optimistically add the user's vehicle into
      // the store, since a successful AuditAddEquipmentToYard call returns empty
      // object {}, and the vehicle in the form doesn't contain the visitId (which
      // we need to add the vehicle to the yard visits slice).
      invalidatesTags: [TagTypes.YardVisits, TagTypes.YardMetrics],
    }),
    forceCheckOut: builder.mutation<ForceCheckOutResponse, ForceCheckOutRequest>({
      query: (request) => ({
        apiName: APIS.ForceCheckOut.name,
        url: APIS.ForceCheckOut.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      invalidatesTags: [
        TagTypes.YardAlerts,
        TagTypes.YardVisits,
        TagTypes.YardMetrics,
        TagTypes.YardJobs,
        TagTypes.YardMoves,
      ],
    }),
    emergencyStatusUpdate: builder.mutation<
      EmergencyTrailerHandoverStatusUpdateResponse,
      EmergencyTrailerHandoverStatusUpdateRequest
    >({
      query: (request) => ({
        apiName: APIS.EmergencyStatusUpdate.name,
        url: APIS.EmergencyStatusUpdate.resource,
        method: "POST",
        body: {
          ...request,
          requester: { system: "RYC" },
        },
      }),
      invalidatesTags: [
        TagTypes.YardAlerts,
        TagTypes.YardVisits,
        TagTypes.YardMetrics,
        TagTypes.YardJobs,
        TagTypes.YardMoves,
      ],
    }),
  }),
});

export const {
  useGetListSupportedYardIdentifiersQuery,
  useGetListEquipmentOwnersQuery,
  useGetYardMapQuery,
  useGetYardAlertsQuery,
  useGetYardJobsQuery,
  useGetYardVisitsQuery,
  useGetYardMetricsQuery,
  useGetYardMovesQuery,
  useGetQueryYardGraphQuery,
  useCompleteYardAlertMutation,
  useBookVRMutation,
  useDismissYardAlertMutation,
  useCreateYardJobMutation,
  useUpdateEquipmentLocationMutation,
  useUpdateEquipmentCoreInfoMutation,
  useUpdateEquipmentOtherInfoMutation,
  useAddOperationalCodesMutation,
  useRemoveOperationalCodesMutation,
  useAuditAddEquipmentToYardMutation,
  useAuditRemoveEquipmentFromYardMutation,
  useForceCheckOutMutation,
  useEmergencyStatusUpdateMutation,
  useLazyGetYardJobsQuery,
  useLazyGetYardAlertsQuery,
  useLazyGetYardMapQuery,
  useLazyGetYardVisitsQuery,
  useLazyGetYardMetricsQuery,
  useLazyGetQueryYardGraphQuery,
  useLazyGetListSupportedYardIdentifiersQuery,
  useLazyGetListEquipmentOwnersQuery,
  useLazyListEligibleLocationsForEquipmentQuery,
  useGetAllFleetEquipmentQuery,
  useLazyGetEquipmentByFleetIdQuery,
  useListSupportedEquipmentTypesQuery,
  useLazyListPendingMovesQuery,
  useCancelMoveMutation,
  useCompleteMoveMutation,
  useReorderMoveMutation,
  useLazyGetYardStateWithPendingMovesQuery,
} = yardApis;
